import { useState, useEffect } from "react";
import { SharedHeader, SharedFooter, PageHero, Breadcrumb, NAVY, ORANGE } from "../../components/site/SharedLayout";
import useScrollToHash from "../../hooks/useScrollToHash";
import { getContactUs } from "../../services/api";
import { API_BASE_URL } from "../../config";
import PageLoader from "../../components/site/PageLoader";
import SEO from "../../components/SEO";


const API_BASE = API_BASE_URL;
/* ─────────────────────────── style tokens ─────────────────────────── */
const NAVY_LIGHT = "#e5ecf9";
const ORANGE_LIGHT = "#fff3e0";
const BG = "#f5f7fb";

const inputBase = {
  width: "100%", padding: "11px 14px", border: `2px solid ${NAVY_LIGHT}`,
  borderRadius: 8, fontSize: 14, fontFamily: "inherit", outline: "none",
  background: "#fff", color: "#222", boxSizing: "border-box",
};
const labelStyle = { display: "block", fontWeight: 600, color: NAVY, marginBottom: 6, fontSize: 13 };
const sectionHeaderStyle = {
  background: NAVY, color: "#fff", padding: "10px 18px", borderRadius: 6,
  fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 20, marginTop: 28,
};

/* ─────────────────────────── shared field helpers ─────────────────── */
const Field = ({ label, required, children, style }) => (
  <div style={style}>
    <label style={labelStyle}>{label}{required && <span style={{ color: ORANGE }}> *</span>}</label>
    {children}
  </div>
);
const FieldAuto = ({ rawLabel, children, style }) => {
  const required = /\*\s*$/.test(rawLabel || "");
  const label = (rawLabel || "").replace(/\s*\*\s*$/, "");
  return <Field label={label} required={required} style={style}>{children}</Field>;
};
const TextInput = ({ name, value, onChange, placeholder, type = "text" }) => (
  <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
    style={inputBase}
    onFocus={e => (e.target.style.borderColor = NAVY)}
    onBlur={e => (e.target.style.borderColor = NAVY_LIGHT)} />
);
const SelectInput = ({ name, value, onChange, children }) => (
  <select name={name} value={value} onChange={onChange}
    style={{ ...inputBase, appearance: "none", cursor: "pointer" }}
    onFocus={e => (e.target.style.borderColor = NAVY)}
    onBlur={e => (e.target.style.borderColor = NAVY_LIGHT)}>
    {children}
  </select>
);
const TextArea = ({ name, value, onChange, placeholder, rows = 5 }) => (
  <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} rows={rows}
    style={{ ...inputBase, resize: "vertical" }}
    onFocus={e => (e.target.style.borderColor = NAVY)}
    onBlur={e => (e.target.style.borderColor = NAVY_LIGHT)} />
);

const SubmitButton = ({ label, mandatoryNote, disabled }) => (
  <div style={{ marginTop: 28 }}>
    <button type="submit" disabled={disabled}
      style={{
        background: disabled ? "#aaa" : ORANGE, color: "#fff", border: "none",
        borderRadius: 8, padding: "14px 36px", fontSize: 15, fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer", transition: "background 0.2s",
      }}
      onMouseEnter={e => { if (!disabled) e.target.style.background = "#d06b1a"; }}
      onMouseLeave={e => { if (!disabled) e.target.style.background = ORANGE; }}>
      {label}
    </button>
    {mandatoryNote && <p style={{ fontSize: 12, color: "#999", marginTop: 10 }}>{mandatoryNote}</p>}
  </div>
);

const SuccessMessage = ({ name, email, type, responseTime }) => (
  <div style={{ textAlign: "center", padding: "48px 0" }}>
    <div style={{ fontSize: 52, marginBottom: 16 }}>📬</div>
    <h2 style={{ color: NAVY, fontWeight: 800, fontSize: 36, marginBottom: 10 }}>Enquiry Submitted!</h2>
    <p style={{ color: "#667", fontSize: 18, lineHeight: 1.7 }}>
      Thank you, <strong>{name}</strong>. Your <strong>{type}</strong> has been received.<br />
      We'll respond to <strong>{email}</strong>{responseTime ? ` within ${responseTime}` : ""}.
    </p>
  </div>
);

/* ══════════════════════ BUSINESS ENQUIRY FORM ═════════════════════════════ */
const BusinessEnquiryForm = ({ form, mandatoryNote, responseTime }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [data, setData] = useState({ name: "", designation: "", company: "", country: "", phone: "", email: "", message: "" });
  const set = f => e => setData(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/submit-contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, subject: form.banner_title, type: "business" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) { setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (submitted) return <SuccessMessage name={data.name} email={data.email} type={form.banner_title} responseTime={responseTime} />;

  return (
    
    <form onSubmit={handleSubmit}>
      <div style={sectionHeaderStyle}>{form.contact_heading}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <FieldAuto rawLabel={form.full_name.label}><TextInput name="name" value={data.name} onChange={set("name")} placeholder={form.full_name.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.designation.label}><TextInput name="designation" value={data.designation} onChange={set("designation")} placeholder={form.designation.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.company.label}><TextInput name="company" value={data.company} onChange={set("company")} placeholder={form.company.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.country.label}><TextInput name="country" value={data.country} onChange={set("country")} placeholder={form.country.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.phone.label}><TextInput name="phone" value={data.phone} onChange={set("phone")} placeholder={form.phone.placeholder} type="tel" /></FieldAuto>
        <FieldAuto rawLabel={form.email.label}><TextInput name="email" value={data.email} onChange={set("email")} placeholder={form.email.placeholder} type="email" /></FieldAuto>
      </div>
      <div style={sectionHeaderStyle}>{form.message.heading}</div>
      <FieldAuto rawLabel={form.message.label} style={{ marginBottom: 8 }}><TextArea name="message" value={data.message} onChange={set("message")} placeholder={form.message.placeholder} rows={6} /></FieldAuto>
      <SubmitButton label={submitting ? "Submitting…" : form.submit_button} mandatoryNote={mandatoryNote} disabled={submitting} />
      {submitError && <p style={{ color: "#c0392b", fontSize: 13, marginTop: 10 }}>⚠ {submitError}</p>}
    </form>
  );
};

/* ══════════════════════ GENERAL ENQUIRY FORM ══════════════════════════════ */
const GeneralEnquiryForm = ({ form, mandatoryNote, responseTime }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [data, setData] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const set = f => e => setData(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/submit-contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, subject: form.banner_title, type: "general" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) { setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (submitted) return <SuccessMessage name={data.name} email={data.email} type={form.banner_title} responseTime={responseTime} />;

  return (
    <form onSubmit={handleSubmit}>
      <div style={sectionHeaderStyle}>{form.contact_heading}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <FieldAuto rawLabel={form.full_name.label}><TextInput name="name" value={data.name} onChange={set("name")} placeholder={form.full_name.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.company.label}><TextInput name="company" value={data.company} onChange={set("company")} placeholder={form.company.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.email.label}><TextInput name="email" value={data.email} onChange={set("email")} placeholder={form.email.placeholder} type="email" /></FieldAuto>
        <FieldAuto rawLabel={form.phone.label}><TextInput name="phone" value={data.phone} onChange={set("phone")} placeholder={form.phone.placeholder} type="tel" /></FieldAuto>
      </div>
      <div style={sectionHeaderStyle}>{form.message.heading}</div>
      <FieldAuto rawLabel={form.message.label} style={{ marginBottom: 8 }}><TextArea name="message" value={data.message} onChange={set("message")} placeholder={form.message.placeholder} rows={6} /></FieldAuto>
      <SubmitButton label={submitting ? "Submitting…" : form.submit_button} mandatoryNote={mandatoryNote} disabled={submitting} />
      {submitError && <p style={{ color: "#c0392b", fontSize: 13, marginTop: 10 }}>⚠ {submitError}</p>}
    </form>
  );
};

/* ══════════════════════ PRODUCT ENQUIRY FORM ══════════════════════════════ */
const ProductEnquiryForm = ({ form, mandatoryNote, responseTime }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [data, setData] = useState({ name: "", designation: "", organisation: "", email: "", phone: "", country: "", productName: "", productCategory: "", quantity: "", message: "" });
  const set = f => e => setData(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/submit-contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, subject: form.banner_title, type: "product" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) { setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (submitted) return <SuccessMessage name={data.name} email={data.email} type={form.banner_title} responseTime={responseTime} />;

  return (
    <form onSubmit={handleSubmit}>
      <div style={sectionHeaderStyle}>{form.contact_heading}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <FieldAuto rawLabel={form.full_name.label}><TextInput name="name" value={data.name} onChange={set("name")} placeholder={form.full_name.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.designation.label}><TextInput name="designation" value={data.designation} onChange={set("designation")} placeholder={form.designation.placeholder || "e.g. Pharmacist, Doctor, Buyer"} /></FieldAuto>
        <FieldAuto rawLabel={form.organization.label}><TextInput name="organisation" value={data.organisation} onChange={set("organisation")} placeholder={form.organization.placeholder || "Organisation name (optional)"} /></FieldAuto>
        <FieldAuto rawLabel={form.country.label}><TextInput name="country" value={data.country} onChange={set("country")} placeholder={form.country.placeholder || "e.g. India"} /></FieldAuto>
        <FieldAuto rawLabel={form.email.label}><TextInput name="email" value={data.email} onChange={set("email")} placeholder={form.email.placeholder} type="email" /></FieldAuto>
        <FieldAuto rawLabel={form.phone.label}><TextInput name="phone" value={data.phone} onChange={set("phone")} placeholder={form.phone.placeholder} type="tel" /></FieldAuto>
      </div>
      <div style={sectionHeaderStyle}>{form.message.heading}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <FieldAuto rawLabel={form.name.label}><TextInput name="productName" value={data.productName} onChange={set("productName")} placeholder={form.name.placeholder} /></FieldAuto>
        <FieldAuto rawLabel={form.category.label}>
          <SelectInput name="productCategory" value={data.productCategory} onChange={set("productCategory")}>
            <option value="">{form.category.placeholder || "— Select category —"}</option>
            <option value="oncology">Oncology</option>
            <option value="injectables">Injectables</option>
            <option value="oral_solids">Oral Solids</option>
            <option value="biologics">Biologics</option>
            <option value="api">API / Bulk Drugs</option>
            <option value="other">Other</option>
          </SelectInput>
        </FieldAuto>
        <FieldAuto rawLabel={form.quantity.label}><TextInput name="quantity" value={data.quantity} onChange={set("quantity")} placeholder={form.quantity.placeholder} /></FieldAuto>
      </div>
      <FieldAuto rawLabel={form.message.label} style={{ marginBottom: 8 }}><TextArea name="message" value={data.message} onChange={set("message")} placeholder={form.message.placeholder} rows={5} /></FieldAuto>
      <SubmitButton label={submitting ? "Submitting…" : form.submit_button} mandatoryNote={mandatoryNote} disabled={submitting} />
      {submitError && <p style={{ color: "#c0392b", fontSize: 13, marginTop: 10 }}>⚠ {submitError}</p>}
    </form>
  );
};

/* ══════════════════════ TAB CARD ═════════════════════════════════════════ */
const TabCard = ({ active, onClick, icon, title, desc, badge }) => (
  <div onClick={onClick} style={{
    background: active ? NAVY : "#fff", color: active ? "#fff" : NAVY,
    padding: "26px 26px 22px", borderRadius: 14,
    border: `2px solid ${active ? NAVY : NAVY_LIGHT}`, cursor: "pointer",
    transition: "all 0.25s ease",
    boxShadow: active ? "0 8px 28px rgba(26,60,110,0.22)" : "0 4px 14px rgba(0,0,0,0.05)",
    position: "relative",
  }}>
    {active && <div style={{ position: "absolute", top: 14, right: 14, width: 10, height: 10, borderRadius: "50%", background: ORANGE }} />}
    <div style={{ width: 44, height: 44, borderRadius: "50%", background: active ? "rgba(224,123,42,0.18)" : NAVY_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14, color: active ? ORANGE : NAVY }}>
      {icon}
    </div>
    <h3 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px 0", lineHeight: 1.3 }}>{title}</h3>
    <p style={{ fontSize: 18, lineHeight: 1.6, margin: "0 0 12px 0", opacity: active ? 0.8 : 0.6 }}>{desc}</p>
    <div style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: active ? "rgba(224,123,42,0.18)" : ORANGE_LIGHT, color: ORANGE, display: "inline-block" }}>
      {badge}
    </div>
  </div>
);

/* ══════════════════════ ICONS ═════════════════════════════════════════════ */
const IconFactory = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M17 18h1" /><path d="M12 18h1" /><path d="M7 18h1" />
  </svg>
);
const IconBuilding = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M12 6h.01" />
    <path d="M12 10h.01" /><path d="M12 14h.01" />
    <path d="M16 10h.01" /><path d="M16 14h.01" />
    <path d="M8 10h.01" /><path d="M8 14h.01" />
  </svg>
);
const IconWarehouse = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z" />
    <path d="M6 18h12" /><path d="M6 14h12" />
    <rect width="12" height="12" x="6" y="10" />
  </svg>
);
const TAB_ICONS = {
  business: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><path d="M12 12h.01" /></svg>),
  general: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>),
  product: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>),
};

/* ══════════════════════ MAIN PAGE ═════════════════════════════════════════ */
export function ContactPage() {
  useScrollToHash();
  const [activeTab, setActiveTab] = useState("business");
  const [contactData, setContactData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getContactUs()
      .then(setContactData)
      .catch(err => setError(err.message || "Failed to load Contact Us page"));
  }, []);

  if (error) return <PageLoader />;
  if (!contactData) return <PageLoader />;
  const seo = contactData?.seo || {};

  const locations = [
    { title: contactData.addresses.registered_office.title, city: contactData.addresses.registered_office.subtitle, address: contactData.addresses.registered_office.address, icon: <IconBuilding /> },
    { title: contactData.addresses.manufacturing_office.title, city: contactData.addresses.manufacturing_office.subtitle, address: contactData.addresses.manufacturing_office.address, icon: <IconFactory /> },
    { title: contactData.addresses.distribution.title, city: contactData.addresses.distribution.subtitle, address: contactData.addresses.distribution.address, icon: <IconWarehouse /> },
  ];
  const tabs = [
    { id: "business", title: contactData.response_times.business.title, desc: contactData.response_times.business.description, badge: contactData.response_times.business.response_time, icon: TAB_ICONS.business },
    { id: "general",  title: contactData.response_times.general.title,  desc: contactData.response_times.general.description,  badge: contactData.response_times.general.response_time,  icon: TAB_ICONS.general },
    { id: "product",  title: contactData.response_times.product.title,  desc: contactData.response_times.product.description,  badge: contactData.response_times.product.response_time,  icon: TAB_ICONS.product },
  ];
  const formBanner = { business: contactData.business_enquiry_form, general: contactData.general_enquiry_form, product: contactData.product_enquiry_form };
  const currentForm = formBanner[activeTab];
  const mandatoryNote = contactData.settings?.mandatory_note || "Fields marked with * are mandatory.";
  const currentResponseTime = contactData.response_times[activeTab]?.response_time;

  return (
    <>
  <SEO
    title={seo.title}
    description={seo.description}
    canonical={seo.canonical}
    robots={seo.robots}
    keywords={seo.keywords}
    ogTitle={seo.ogTitle}
    ogDescription={seo.ogDescription}
    ogImage={seo.ogImage}
    twitterTitle={seo.twitterTitle}
    twitterDescription={seo.twitterDescription}
    twitterImage={seo.twitterImage}
    author="GLS Pharma"
  publisher="GLS Pharma"
  />
    <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", margin: 0, padding: 0, color: "#222" }}>
      <SharedHeader />
      <Breadcrumb items={[{ label: "Home", path: "/" }, { label: "Contact Us", path: "/contact" }]} />
      <PageHero label={contactData.hero.small_heading} title={contactData.hero.main_heading} subtitle={contactData.hero.description} bgImage={contactData.hero.background_image?.url} />

      {/* ── LOCATIONS ── */}
      <section id="locations" style={{ padding: "70px 80px 50px", background: BG, scrollMarginTop: 80 }}>
        <div style={{ marginBottom: 30 }}>
          <div style={{ color: ORANGE, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{contactData.office_section.subtitle}</div>
          <h2 style={{ color: NAVY, fontWeight: 800, fontSize: 36, margin: 0 }}>{contactData.office_section.title}</h2>
        </div>
        <div className="contact-location-grid" style={{ display: "grid", gap: 24 }}>
          {locations.map((loc, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e5ecf9", borderRadius: 20, padding: "28px 26px", boxShadow: "0 6px 20px rgba(0,0,0,0.05)", transition: "all 0.3s ease" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.05)"; }}>
              <div style={{ width: 52, height: 52, background: "#eef2f8", color: ORANGE, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>{loc.icon}</div>
              <div style={{ color: ORANGE, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>{loc.title}</div>
              <h3 style={{ color: NAVY, fontWeight: 800, fontSize: 20, margin: "0 0 12px" }}>{loc.city}</h3>
              <div style={{ color: "#556", fontSize: 18, lineHeight: 1.7 }}>{loc.address}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ENQUIRY FORMS ── */}
      <section id="enquiry" style={{ padding: "50px 80px 80px", background: BG, scrollMarginTop: 80 }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{ color: ORANGE, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{contactData.enquiry_section.subtitle}</div>
          <h2 style={{ color: NAVY, fontWeight: 800, fontSize: 36, margin: 0 }}>{contactData.enquiry_section.title}</h2>
        </div>
        <div className="contact-tab-grid" style={{ display: "grid", gap: 24, marginBottom: 36 }}>
          {tabs.map(tab => (
            <TabCard key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} icon={tab.icon} title={tab.title} desc={tab.desc} badge={tab.badge} />
          ))}
        </div>
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 10px 40px rgba(0,0,0,0.08)", border: `1px solid ${NAVY_LIGHT}`, overflow: "hidden" }}>
          <div style={{ background: NAVY, padding: "14px 28px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: ORANGE, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#fff", display: "flex" }}>{TAB_ICONS[activeTab]}</span>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: ORANGE, letterSpacing: "0.08em", textTransform: "uppercase" }}>{currentForm.banner_title}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>{currentForm.banner_description}</div>
            </div>
          </div>
          <div style={{ padding: "32px 40px" }}>
            {activeTab === "business" && <BusinessEnquiryForm form={currentForm} mandatoryNote={mandatoryNote} responseTime={currentResponseTime} />}
            {activeTab === "general"  && <GeneralEnquiryForm  form={currentForm} mandatoryNote={mandatoryNote} responseTime={currentResponseTime} />}
            {activeTab === "product"  && <ProductEnquiryForm  form={currentForm} mandatoryNote={mandatoryNote} responseTime={currentResponseTime} />}
          </div>
        </div>
      </section>

      <SharedFooter />
    </div>
    </>
  );
}

export default ContactPage;
